import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert, Button, Card, CardContent, CardDescription, CardHeader, CardTitle,
  Input, Label, Spinner,
} from '../../components/ui';
import { useAuth } from '../../contexts/AuthContext';

function getPasswordError(error: unknown): string {
  const detail = (error as { response?: { data?: { detail?: unknown } } })
    ?.response?.data?.detail;
  if (typeof detail === 'string' && detail.trim()) return detail;
  return 'Unable to change your password. Please try again.';
}

export default function ChangePasswordPage() {
  const navigate = useNavigate();
  const { user, changePassword } = useAuth();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isComplete, setIsComplete] = useState(false);
  const passwordsMatch = newPassword === confirmation;
  const canSubmit = currentPassword.length > 0 && newPassword.length >= 8
    && passwordsMatch && !isSubmitting;

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!canSubmit) return;
    setError(null);
    if (newPassword === currentPassword) {
      setError('Choose a different password from your current password.');
      return;
    }
    setIsSubmitting(true);
    try {
      await changePassword({ current_password: currentPassword, new_password: newPassword });
      setCurrentPassword('');
      setNewPassword('');
      setConfirmation('');
      setIsComplete(true);
    } catch (changeError) {
      setError(getPasswordError(changeError));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-dark-bg flex items-center justify-center px-4 py-8">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle>Change Your Password</CardTitle>
          <CardDescription>
            Update the password for your Eliza account{user?.email ? ` (${user.email})` : ''}.
            If you sign in only with SSO, manage your password with your identity provider.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isComplete ? (
            <div className="space-y-4">
              <Alert variant="success">Your password has been changed successfully.</Alert>
              <Button className="w-full" onClick={() => navigate('/home', { replace: true })}>
                Back to Home
              </Button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4" aria-busy={isSubmitting}>
              {error ? <Alert variant="error">{error}</Alert> : null}
              <div className="space-y-2">
                <Label htmlFor="current_password">Current Password</Label>
                <Input id="current_password" type="password" autoComplete="current-password"
                  value={currentPassword} onChange={(event) => setCurrentPassword(event.target.value)}
                  required autoFocus disabled={isSubmitting} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="new_password">New Password</Label>
                <Input id="new_password" type="password" autoComplete="new-password"
                  value={newPassword} onChange={(event) => setNewPassword(event.target.value)}
                  required minLength={8} disabled={isSubmitting} aria-describedby="password_hint" />
                <p id="password_hint" className="text-xs text-gray-500 dark:text-gray-400">
                  Use a strong, unique password with at least 8 characters.
                  Your account's password policy may require more.
                </p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="confirm_password">Confirm New Password</Label>
                <Input id="confirm_password" type="password" autoComplete="new-password"
                  value={confirmation} onChange={(event) => setConfirmation(event.target.value)}
                  required minLength={8} disabled={isSubmitting}
                  aria-invalid={confirmation.length > 0 && !passwordsMatch}
                  aria-describedby={confirmation.length > 0 && !passwordsMatch ? 'password_mismatch' : undefined} />
                {confirmation.length > 0 && !passwordsMatch ? (
                  <p id="password_mismatch" className="text-xs text-red-600 dark:text-red-400">
                    Passwords do not match.
                  </p>
                ) : null}
              </div>
              <Button type="submit" className="w-full" disabled={!canSubmit}>
                {isSubmitting ? <Spinner size="sm" className="mr-2" /> : null}
                {isSubmitting ? 'Changing Password…' : 'Change Password'}
              </Button>
              <Button type="button" variant="ghost" className="w-full" disabled={isSubmitting}
                onClick={() => navigate('/home')}>
                Cancel
              </Button>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
